import { useState, useEffect, useRef } from 'react';

/**
 * Custom React hook to dynamically calculate the available height for an element
 * from its top bounding rect to the bottom of the viewport.
 * 
 * @param {Array} dependencies Dependency array to trigger height recalculations (e.g. state toggling showing/hiding other components)
 * @param {number} offset Bottom offset margin in pixels (default 40px)
 * @param {number} minHeight Minimum height in pixels to fallback to (default 300px)
 */
export default function useDynamicHeight(dependencies = [], offset = 40, minHeight = 300) {
  const [height, setHeight] = useState(500);
  const elementRef = useRef(null);

  useEffect(() => {
    let animationFrameId;

    const handleResize = () => {
      animationFrameId = requestAnimationFrame(() => {
        if (elementRef.current) {
          const rect = elementRef.current.getBoundingClientRect();
          const topOffset = rect.top;
          const windowHeight = window.innerHeight;
          const calculatedHeight = windowHeight - topOffset - offset;
          setHeight(Math.max(minHeight, calculatedHeight));
        }
      });
    };

    // Calculate height after layout paint
    const timeoutId = setTimeout(handleResize, 50);

    window.addEventListener('resize', handleResize);
    return () => {
      clearTimeout(timeoutId);
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener('resize', handleResize);
    };
  }, [offset, minHeight, ...dependencies]);

  return [elementRef, height];
}
